import {Injectable, OnDestroy} from '@angular/core';
import {ObjectsProvider} from '@providers/objects/objects.provider';
import {MethodsProvider} from '@providers/methods/methods.provider';
import {FormControl} from '@angular/forms';
import {BehaviorSubject} from 'rxjs';
import {WizardInterfaces} from '../../wizard.namespace';
import {combineLatest} from 'rxjs';
import {SpecialistsProvider} from '@providers/specialists/specialists.provider';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {ComplianceService} from './compliance.service';
import CompliationData = WizardInterfaces.CompliationData;
import {WizardFormEventsService} from './wizard-form-events.service';
import {Subject} from 'rxjs';
import {ErrorLoggerService} from '@services/error-logger.service';
import {WizardSpecialist, WizardSpecialistCompetence} from '../../models/wizard-specialist';

@Injectable()
export class ComplianceCalculateService implements OnDestroy {
    public fbControl: FormControl = new FormControl(false);

    /**
     * Рассчитанные развернутые данные таблицы перекрытия области аттестации, для отображения во View
     */
    complianceTableData$ = new BehaviorSubject<CompliationData[]>([]);

    attestation: WizardInterfaces.AttestationValue[] = [];
    personalIds: number[] = [];

    public specialists$ = new BehaviorSubject<WizardSpecialist[]>([]);

    private preventSubscribe = new Subject();

    constructor(
        private objectsProvider: ObjectsProvider,
        private methodsProvider: MethodsProvider,
        private specialistsProvider: SpecialistsProvider,
        private service: ComplianceService,
        private wizardFormEventsService: WizardFormEventsService,
        private logger: ErrorLoggerService,
    ) {
        this.storeSpecialists([]);
    }

    init() {
        /**
         * Для расчета нужны:
         * * Объекты
         * * Методы
         * * Список неуволенных специалистов
         * * Заявленная область аттестации
         * * Id специалистов, выбранных в качестве персонала
         */
        const combined = combineLatest(
            this.objectsProvider.data$,
            this.methodsProvider.data$,
            this.specialistsProvider.notFired$,
            this.wizardFormEventsService.attestationChange$,
            this.wizardFormEventsService.staffIdsChange$,
        );

        combined.pipe(
            takeUntil(this.preventSubscribe),
            debounceTime(300)
        ).subscribe((value) => {
            try {
                const objects = value[0];
                const methods = value[1];
                this.attestation = value[3];
                this.personalIds = value[4].map(id => +id);

                /**
                 * Из всех существующих неуволенных специалистов получаем только тех,
                 * которые выбраны в визарде и создаем из них экземпляры класса для расчета таблицы соответствия
                 */
                const specialists = value[2];
                const selectedSpecialists = specialists.filter(spec => this.personalIds.includes(+spec.id));
                const calculatedSpecialists = selectedSpecialists.map(spec => {
                    return new WizardSpecialist(spec, objects.asTree, methods.asTree, this.attestation);
                });

                this.storeSpecialists(calculatedSpecialists);
                this.calculateTable(calculatedSpecialists.map(spec => spec.competence));
            } catch (e) {
                this.logger.log(e, {
                    message: `Cannot calculate compliance table with parameters:`,
                    data: value
                });
                this.fbControl.patchValue(false, {onlySelf: true});
                this.fbControl.markAsTouched();
                this.fbControl.markAsDirty();
                this.fbControl.updateValueAndValidity();
            }
        });
    }

    ngOnDestroy() {
        this.preventSubscribe.next();
    }

    private storeSpecialists(list: WizardSpecialist[] = []) {
        this.specialists$.next(list);
    }

    private calculateTable(specialistsCompetence: WizardSpecialistCompetence[] = []) {
        if (!this.fbControl) {
            return;
        }

        this.service.objects = this.objectsProvider.data$.getValue().asTree;
        this.service.methods = this.methodsProvider.data$.getValue().asTree;

        const value = this.service.mapAttestationValue(this.attestation);

        const data = this.service.calculateCompetence(value, specialistsCompetence);

        const isValid = this.service.isDataValid(data);

        this.complianceTableData$.next(data);

        this.fbControl.patchValue(isValid, {onlySelf: true});
        this.fbControl.markAsTouched();
        this.fbControl.markAsDirty();
        this.fbControl.updateValueAndValidity();
    }
}
